import { useCallback, useEffect, useState, type ReactElement } from "react";
import {
  TOKEN_ROUTE_BASE,
  checkBffSession,
  checkRuntimeReady,
  fetchBffMe,
  getOidcLoginUrl,
  openOidcLoginNewTab,
  parseEmails,
  uniqueDomSuffix,
} from "./sandbox-utils.js";

function IconPlus(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width={18} height={18} aria-hidden>
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 5v14M5 12h14"
      />
    </svg>
  );
}

function IconLogout(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width={18} height={18} aria-hidden>
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"
      />
    </svg>
  );
}

function IconTrash(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width={15} height={15} aria-hidden>
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
      />
    </svg>
  );
}

interface PanelSpec {
  panelId: string;
  emailKey: string;
  display: string;
}

function MonitorPanelCard({
  panelId,
  emailKey,
  display,
  onRemovePanel,
}: {
  panelId: string;
  emailKey: string;
  display: string;
  onRemovePanel: (key: string) => void;
}): ReactElement {
  const hostId = `host-${panelId}`;

  useEffect(() => {
    let instance: { destroy(): Promise<void> } | undefined;
    let cancelled = false;

    void (async () => {
      const ready = checkRuntimeReady();
      if (cancelled) return;
      if (!ready.ok) {
        console.warn(ready.reason);
        return;
      }

      if (cancelled) return;

      const tokenPath = `${TOKEN_ROUTE_BASE}/${encodeURIComponent(display)}`;
      try {
        instance = ready.create({
          container: `#${hostId}`,
          hubUrl: ready.hubUrl,
          tokenPath,
        });
      } catch (e: unknown) {
        console.error(e);
        window.alert(`No se pudo crear el widget para ${display}`);
        onRemovePanel(emailKey);
      }
    })();

    return () => {
      cancelled = true;
      void Promise.resolve(instance?.destroy?.())
        .catch(() => {
          /* ignore */
        });
    };
  }, [panelId, display, emailKey, onRemovePanel]);

  return (
    <article className="monitor-card" id={`monitor-panel-${panelId}`}>
      <div className="monitor-card-head">
        <span className="monitor-card-email">{display}</span>
        <button
          type="button"
          className="monitor-card-remove"
          aria-label="Eliminar instancia"
            title="Eliminar"
            onClick={() => {
              onRemovePanel(emailKey);
            }}
        >
          <IconTrash />
        </button>
      </div>
      <div className="monitor-card-body">
        <div id={hostId} />
      </div>
    </article>
  );
}

export function App(): ReactElement {
  const [agentsOpen, setAgentsOpen] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [panels, setPanels] = useState<PanelSpec[]>([]);
  const [sessionRenewalNeeded, setSessionRenewalNeeded] = useState(false);
  /** `undefined` = aún no cargó `GET /auth/me`; string (vacío posible) = ya respondió. */
  const [sessionUserLabel, setSessionUserLabel] = useState<string | undefined>(
    undefined,
  );

  useEffect(() => {
    let alive = true;
    void fetchBffMe().then((m) => {
      if (!alive) return;
      if (!m) {
        setSessionUserLabel("");
        return;
      }
      const label =
        [m.email, m.user, m.name, m.sub].find(
          (x): x is string => typeof x === "string" && x.trim().length > 0,
        )?.trim() ?? "";
      setSessionUserLabel(label);
    });
    return () => {
      alive = false;
    };
  }, []);

  const removePanel = useCallback((emailKey: string) => {
    setPanels((prev) => prev.filter((p) => p.emailKey !== emailKey));
  }, []);

  const onAddAgents = useCallback(async () => {
    const ready = checkRuntimeReady();
    if (!ready.ok) {
      window.alert(ready.reason);
      return;
    }
    const parsed = parseEmails(emailInput);
    if (parsed.length === 0) {
      window.alert("Escribí un correo de agente por línea.");
      return;
    }
    if (!(await checkBffSession())) {
      setSessionRenewalNeeded(true);
      setAgentsOpen(true);
      return;
    }
    setSessionRenewalNeeded(false);
    setPanels((prev) => {
      const existing = new Set(prev.map((p) => p.emailKey));
      const add: PanelSpec[] = [];
      for (const { key, display } of parsed) {
        if (existing.has(key)) continue;
        existing.add(key);
        add.push({
          panelId: uniqueDomSuffix(),
          emailKey: key,
          display,
        });
      }
      return [...prev, ...add];
    });
    setEmailInput("");
    setAgentsOpen(false);
  }, [emailInput]);

  const onLogout = useCallback(() => {
    void fetch("/auth/logout", { method: "POST", credentials: "include" })
      .catch(() => {
        /* redirigir igual */
      })
      .finally(() => {
        window.location.href = "/login.html";
      });
  }, []);

  return (
    <div className="sandbox-shell">
      <header className="sandbox-navbar">
        <h1 className="sandbox-navbar-title">Aircall Monitor</h1>
        <div className="sandbox-navbar-actions">
          <button
            type="button"
            id="btn-toggle-agents"
            className="sandbox-icon-btn"
            aria-label="Añadir agentes"
            title="Añadir agentes"
            aria-expanded={agentsOpen}
            aria-controls="sandbox-agents-panel"
            onClick={() => {
              setAgentsOpen((o) => !o);
            }}
          >
            <IconPlus />
          </button>
          <div className="sandbox-navbar-session">
            {sessionUserLabel !== undefined && sessionUserLabel !== "" ? (
              <span
                className="sandbox-navbar-email"
                title={`Sesión OIDC: ${sessionUserLabel}`}
              >
                {sessionUserLabel}
              </span>
            ) : null}
            <button
              type="button"
              id="btn-logout"
              className="sandbox-icon-btn"
              aria-label={
                sessionUserLabel
                  ? `Cerrar sesión (${sessionUserLabel})`
                  : "Cerrar sesión"
              }
              title={
                sessionUserLabel
                  ? `Cerrar sesión (${sessionUserLabel})`
                  : "Cerrar sesión"
              }
              onClick={onLogout}
            >
              <IconLogout />
            </button>
          </div>
        </div>
      </header>

      <div
        id="sandbox-agents-panel"
        className="sandbox-agents-panel"
        role="region"
        aria-label="Añadir agentes"
        hidden={!agentsOpen}
      >
        <section className="sandbox-controls">
          {sessionRenewalNeeded ? (
            <div className="sandbox-session-hint" role="status">
              <p className="sandbox-session-hint-title">Hay que volver a validar la sesión con el servidor</p>
              <p className="sandbox-session-hint-text">
                No recargamos esta página para no cerrar los widgets que ya tenés. Podés abrir el
                inicio de sesión en otra pestaña; al terminar, las cookies aplican aquí. Después
                pulsá «Comprobar de nuevo» o volvé a intentar «Añadir agentes».
              </p>
              <div className="sandbox-session-hint-actions">
                <button
                  type="button"
                  className="sandbox-btn-primary"
                  onClick={() => {
                    openOidcLoginNewTab();
                  }}
                >
                  Abrir login (nueva pestaña)
                </button>
                <button
                  type="button"
                  className="sandbox-btn-secondary"
                  onClick={() => {
                    void (async () => {
                      if (await checkBffSession()) {
                        setSessionRenewalNeeded(false);
                      } else {
                        window.alert(
                          "Sigue sin haber sesión válida. Abrí el login en otra pestaña, identificate y probá otra vez.",
                        );
                      }
                    })();
                  }}
                >
                  Comprobar de nuevo
                </button>
              </div>
              <p className="sandbox-session-hint-fallback">
                <a href={getOidcLoginUrl()} target="_blank" rel="noopener noreferrer">
                  Abrir login (enlace)
                </a>{" "}
                si el navegador bloquea ventanas nuevas.
              </p>
            </div>
          ) : null}
          <label htmlFor="emails-input">Correos de agentes (uno por línea)</label>
          <textarea
            id="emails-input"
            autoComplete="email"
            placeholder={"Ej: uno@test.com\notro@empresa.es"}
            spellCheck={false}
            value={emailInput}
            onChange={(e) => {
              setEmailInput(e.target.value);
            }}
          />
          <div className="sandbox-actions">
            <button type="button" id="btn-add-emails" onClick={onAddAgents}>
              Añadir agentes
            </button>
          </div>
        </section>
      </div>

      <section id="monitor-stack" className="monitor-stack" aria-live="polite">
        {panels.map((p) => (
          <MonitorPanelCard
            key={p.panelId}
            panelId={p.panelId}
            emailKey={p.emailKey}
            display={p.display}
            onRemovePanel={removePanel}
          />
        ))}
      </section>
    </div>
  );
}
